// 정규식 상태(선택한 모드/필드)를 짧은 텍스트 코드로 인코딩/디코딩.
// 정규식 문자열 대신 모드 목록의 고유번호(no)만 담아서 코드를 짧게 유지한다.
// 지도/경로석은 접두어·접미어를 하나의 16비트 정수로 합쳐서 저장한다: 최상위 비트(0x8000)로
// 접두어/접미어를 구분하고 나머지 15비트에 no를 담는다. no를 오프셋으로 더하는 방식은
// "악몽 맵모드"가 no:1001+ 같은 높은 번호를 이미 쓰고 있어서 충돌이 났었음(비트 분리로 해결).
import { regexCalc } from './fnRegexCalc';

const SUFFIX_FLAG = 0x8000;

export const PAGE_TYPE = {
  MAP: 0,
  WAYSTONE: 1,
  TABLET: 2,
};

export const NUMERIC_FIELDS = {
  [PAGE_TYPE.MAP]: ['quantity', 'packSize', 'scarab', 'currency', 'map'],
  [PAGE_TYPE.WAYSTONE]: ['itemRarity', 'packSize', 'monsterRarity', 'monsterEfficiency', 'waystoneChance'],
};

// MapOption.js / WaystoneOption.js와 동일한 접두 정규식. 원본 숫자만 코드에 담고
// 불러올 때 이 접두어 + regexCalc(숫자)로 다시 조립한다(문자열 자체를 담는 것보다 훨씬 짧음).
const FIELD_PREFIX = {
  [PAGE_TYPE.MAP]: {
    quantity: '수량:.*',
    packSize: '규모:.*',
    scarab: '갑.*',
    currency: '화.*',
    map: '지도.*',
  },
  [PAGE_TYPE.WAYSTONE]: {
    itemRarity: '템.*도:.*',
    packSize: '규모:.*',
    monsterRarity: '터.*도:.*',
    monsterEfficiency: '율:.*',
    waystoneChance: '률:.*',
  },
};

const CATALOG_VERSION = {
  [PAGE_TYPE.MAP]: 'poe1',
  [PAGE_TYPE.WAYSTONE]: 'poe2',
};

class ByteWriter {
  constructor() {
    this.bytes = [];
  }
  u8(v) {
    this.bytes.push(v & 0xff);
  }
  u16(v) {
    this.bytes.push((v >> 8) & 0xff, v & 0xff);
  }
  str(s) {
    const encoded = Array.from(new TextEncoder().encode(s || ''));
    this.u8(encoded.length);
    this.bytes.push(...encoded);
  }
  toBytes() {
    return new Uint8Array(this.bytes);
  }
}

class ByteReader {
  constructor(bytes) {
    this.bytes = bytes;
    this.pos = 0;
  }
  u8() {
    return this.bytes[this.pos++];
  }
  u16() {
    const v = (this.bytes[this.pos] << 8) | this.bytes[this.pos + 1];
    this.pos += 2;
    return v;
  }
  str() {
    const len = this.u8();
    const slice = this.bytes.slice(this.pos, this.pos + len);
    this.pos += len;
    return new TextDecoder().decode(slice);
  }
}

function bytesToBase64Url(bytes) {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlToBytes(code) {
  let b64 = code.trim().replace(/-/g, '+').replace(/_/g, '/');
  while (b64.length % 4) b64 += '=';
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function loadMapCatalog(pageType) {
  const version = CATALOG_VERSION[pageType];
  const [prefixModule, suffixModule] = await Promise.all([
    import(`../MapOptionData/${version}/mapModPrefix`),
    import(`../MapOptionData/${version}/mapModSuffix`),
  ]);
  return { prefix: prefixModule.default || [], suffix: suffixModule.default || [] };
}

async function loadTabletCatalog() {
  const tabletModule = await import('../MapOptionData/poe2/tabletMod');
  return tabletModule.default || [];
}

/**
 * @param {object} rawFieldValues - { [fieldName]: 사용자가 입력창에 입력한 원래 숫자 문자열 }
 *   (계산된 정규식 문자열이 아니라 원본 숫자만 넘겨야 코드가 짧아짐)
 */
export async function encodeMapShare(pageType, result, regexOperator, rawFieldValues = {}) {
  const { prefix, suffix } = await loadMapCatalog(pageType);
  const regexToIndex = new Map();
  prefix.forEach((m) => regexToIndex.set(m.regex, m.no));
  suffix.forEach((m) => regexToIndex.set(m.regex, m.no | SUFFIX_FLAG));

  const wantIdx = (result.wantMod || []).map((r) => regexToIndex.get(r)).filter((v) => v !== undefined);
  const ignoreIdx = (result.ignoreMod || []).map((r) => regexToIndex.get(r)).filter((v) => v !== undefined);

  const fields = NUMERIC_FIELDS[pageType];
  const rawValues = fields.map((f) => {
    if (!result[f]) return null;
    const n = parseInt(rawFieldValues[f], 10);
    if (!Number.isFinite(n)) return null;
    return Math.min(Math.max(n, 0), 0xffff);
  });

  let flags = regexOperator === 'AND' ? 1 : 0;
  rawValues.forEach((v, i) => {
    if (v !== null) flags |= 1 << (i + 1);
  });

  const w = new ByteWriter();
  w.u8(pageType);
  w.u8(flags);
  w.u8(wantIdx.length);
  wantIdx.forEach((i) => w.u16(i));
  w.u8(ignoreIdx.length);
  ignoreIdx.forEach((i) => w.u16(i));
  rawValues.forEach((v) => {
    if (v !== null) w.u16(v);
  });

  return bytesToBase64Url(w.toBytes());
}

async function decodeMapShare(reader, pageType) {
  const flags = reader.u8();
  const regexOperator = flags & 1 ? 'AND' : 'OR';

  const { prefix, suffix } = await loadMapCatalog(pageType);
  const indexToRegex = new Map();
  prefix.forEach((m) => indexToRegex.set(m.no, m.regex));
  suffix.forEach((m) => indexToRegex.set(m.no | SUFFIX_FLAG, m.regex));

  const wantMod = [];
  const wantCount = reader.u8();
  for (let i = 0; i < wantCount; i++) {
    const regex = indexToRegex.get(reader.u16());
    if (regex) wantMod.push(regex);
  }

  const ignoreMod = [];
  const ignoreCount = reader.u8();
  for (let i = 0; i < ignoreCount; i++) {
    const regex = indexToRegex.get(reader.u16());
    if (regex) ignoreMod.push(regex);
  }

  const fields = NUMERIC_FIELDS[pageType];
  const prefixMap = FIELD_PREFIX[pageType];
  const result = { wantMod, ignoreMod };
  const rawFields = {};
  fields.forEach((f, i) => {
    if (flags & (1 << (i + 1))) {
      const raw = reader.u16();
      const calc = regexCalc(raw);
      result[f] = calc ? prefixMap[f] + calc : '';
      rawFields[f] = String(raw);
    } else {
      result[f] = '';
    }
  });

  return { pageType, regexOperator, result, rawFields };
}

export async function encodeTabletShare(result, lvList) {
  const tabletMod = await loadTabletCatalog();
  const regexToNo = new Map();
  tabletMod.forEach((m) => regexToNo.set(m.regex, m.no));

  const orIdx = (result.orMod || []).map((r) => regexToNo.get(r)).filter((v) => v !== undefined);
  const andIdx = (result.andMod || []).map((r) => regexToNo.get(r)).filter((v) => v !== undefined);
  const category = result.category || [];

  let catBits = 0;
  category.forEach((c) => {
    const bit = lvList.indexOf(c);
    if (bit >= 0) catBits |= 1 << bit;
  });

  const w = new ByteWriter();
  w.u8(PAGE_TYPE.TABLET);
  w.u8(catBits);
  w.u8(orIdx.length);
  orIdx.forEach((i) => w.u16(i));
  w.u8(andIdx.length);
  andIdx.forEach((i) => w.u16(i));

  return bytesToBase64Url(w.toBytes());
}

async function decodeTabletShare(reader, lvList) {
  const catBits = reader.u8();
  const tabletMod = await loadTabletCatalog();
  const noToRegex = new Map();
  tabletMod.forEach((m) => noToRegex.set(m.no, m.regex));

  const orMod = [];
  const orCount = reader.u8();
  for (let i = 0; i < orCount; i++) {
    const regex = noToRegex.get(reader.u16());
    if (regex) orMod.push(regex);
  }

  const andMod = [];
  const andCount = reader.u8();
  for (let i = 0; i < andCount; i++) {
    const regex = noToRegex.get(reader.u16());
    if (regex) andMod.push(regex);
  }

  const category = lvList.filter((_, i) => catBits & (1 << i));

  return { pageType: PAGE_TYPE.TABLET, result: { orMod, andMod, category } };
}

/**
 * @param {string} code
 * @param {string[]} tabletLvList - TabletModList.LV_LIST (서판 코드 디코딩 시에만 필요)
 */
export async function decodeShareCode(code, tabletLvList) {
  const bytes = base64UrlToBytes(code);
  if (bytes.length === 0) throw new Error('empty code');
  const reader = new ByteReader(bytes);
  const pageType = reader.u8();

  if (pageType === PAGE_TYPE.TABLET) {
    return decodeTabletShare(reader, tabletLvList || []);
  }
  if (pageType === PAGE_TYPE.MAP || pageType === PAGE_TYPE.WAYSTONE) {
    return decodeMapShare(reader, pageType);
  }
  throw new Error('unknown page type');
}
